import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { ArrowDown, Star, Play, Pause, Volume2 } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
function useCountdown() {
  const getDeadline = () => {
    const stored = localStorage.getItem('memorial-deadline');
    if (stored) {
      const deadline = parseInt(stored, 10);
      if (deadline > Date.now()) return deadline;
    }
    // Set deadline to 23h 59m 59s from now (rolling ~24h window)
    const deadline = Date.now() + 24 * 60 * 60 * 1000 - 1000;
    localStorage.setItem('memorial-deadline', String(deadline));
    return deadline;
  };
  const [deadline] = useState(getDeadline);
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, deadline - Date.now()));
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, deadline - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [deadline]);
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor(timeLeft % (1000 * 60 * 60) / (1000 * 60));
  const seconds = Math.floor(timeLeft % (1000 * 60) / 1000);
  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0')
  };
}
// Custom Audio Player Component
function PrologueAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration;
      setCurrentTime(current);
      setProgress(current / total * 100);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressBarRef.current && audioRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, x / rect.width * 100));
      const newTime = percentage / 100 * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setProgress(percentage);
      setCurrentTime(newTime);
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <div className="w-full max-w-md mx-auto mb-10 bg-surface border border-border-accent/50 rounded-lg p-4 shadow-gold-subtle relative overflow-hidden group transition-all duration-300 hover:border-accent-primary/80" data-id="element-363">
      {/* Subtle background glow when playing */}
      <div className={`absolute inset-0 bg-accent-primary/5 transition-opacity duration-700 pointer-events-none ${isPlaying ? 'opacity-100' : 'opacity-0'}`} data-id="element-364" />

      <audio ref={audioRef} src="https://cdn.magicpatterns.com/uploads/8mFvu6iRP1awtRBf43zPee/THE%20VOICE%20THEY%20COULD%202minutosAUDIO.mp4" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)} className="hidden" data-id="element-365" />

      <div className="relative z-10 flex flex-col gap-3" data-id="element-366">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-367">
          <div className="flex items-center gap-2" data-id="element-368">
            <Volume2 className="w-4 h-4 text-accent-primary" data-id="element-369" />
            <span className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-primary" data-id="element-370">
              Listen to the Prologue
            </span>
          </div>
          <span className="font-mono text-xs text-text-muted" data-id="element-371">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        {/* Controls & Progress */}
        <div className="flex items-center gap-4" data-id="element-372">
          <button onClick={togglePlay} className="flex-shrink-0 w-10 h-10 rounded-full bg-elevated border border-accent-primary/40 flex items-center justify-center text-accent-primary hover:bg-accent-primary hover:text-base transition-colors duration-300 focus:outline-none" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-373">
            {isPlaying ? <Pause className="w-4 h-4 fill-current" data-id="element-374" /> : <Play className="w-4 h-4 fill-current ml-0.5" data-id="element-375" />}
          </button>

          <div className="flex-grow h-2 bg-elevated rounded-full overflow-hidden cursor-pointer border border-border-default relative" ref={progressBarRef} onClick={handleProgressClick} data-id="element-376">
            <div className="absolute top-0 left-0 h-full bg-gradient-gold transition-all duration-100 ease-linear" style={{
            width: `${progress}%`
          }} data-id="element-377" />
          </div>
        </div>
      </div>
    </div>;
}
export function HeroSection() {
  const countdown = useCountdown();
  const scrollToNext = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex flex-col items-center justify-center pt-20 pb-8 px-6 bg-base overflow-hidden" data-id="element-378">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-primary/5 rounded-full blur-[120px] pointer-events-none" data-id="element-379" />

      <div className="max-w-5xl mx-auto w-full flex flex-col items-center text-center z-10 mt-2" data-id="element-380">
        <FadeIn delay={0.2} data-id="element-381">
          <span className="inline-block font-ui text-xs font-medium tracking-widest uppercase text-accent-soft border border-accent-muted rounded-full px-4 py-1 mb-5" data-id="element-382">
            Memorial Edition
          </span>
        </FadeIn>

        <FadeIn delay={0.4} data-id="element-383">
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-text-primary leading-tight tracking-tight mb-4 max-w-4xl mx-auto" data-id="element-384">
            The Voice They Couldn't Silence
          </h1>
        </FadeIn>

        <FadeIn delay={0.6} data-id="element-385">
          <p className="font-body text-lg md:text-xl text-text-secondary max-w-2xl mx-auto mb-8" data-id="element-386">
            Some stories deserve more than 48 hours.
          </p>
        </FadeIn>

        <FadeIn delay={0.8} className="relative w-full max-w-md md:max-w-lg mx-auto mb-8" data-id="element-387">
          <div className="relative aspect-[4/3] w-full flex justify-center" data-id="element-388">
            <div className="rounded-3xl overflow-hidden" data-id="element-389">
              <img src="https://cdn.magicpatterns.com/uploads/bx8zXpS1o37kRzPKWiqoqb/mockup.png" alt="The Voice They Couldn't Silence Book Mockup" className="w-full h-full object-cover drop-shadow-2xl" data-id="element-390" />
            </div>
          </div>
        </FadeIn>

        {/* Audio Player Banner */}
        <FadeIn delay={0.9} className="w-full" data-id="element-391">
          <PrologueAudioPlayer data-id="element-392" />
        </FadeIn>

        <FadeIn delay={1.0} data-id="element-393">
          <p className="font-body text-xl md:text-2xl text-text-primary mb-6 max-w-lg mx-auto" data-id="element-394">
            Be the person who knows the full story.
          </p>
          <Button href="#offer" className="mb-6" data-id="element-395">
            Get The Memorial Edition &rarr;
          </Button>

          {/* Urgency Countdown */}
          <div className="mb-6" data-id="element-396">
            <p className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-soft mb-3" data-id="element-397">
              Memorial Edition Closes In
            </p>
            <div className="inline-flex items-center gap-3 md:gap-4 bg-elevated/60 border border-border-default rounded-sm px-5 py-3 shadow-gold-subtle" data-id="element-398">
              <div className="flex flex-col items-center" data-id="element-399">
                <span className="font-mono text-2xl md:text-3xl font-bold text-accent-primary leading-none tabular-nums" data-id="element-400">
                  {countdown.hours}
                </span>
                <span className="font-ui text-[9px] uppercase tracking-widest text-text-muted mt-1" data-id="element-401">
                  Hours
                </span>
              </div>
              <span className="font-mono text-xl text-accent-primary/50 -mt-3" data-id="element-402">
                :
              </span>
              <div className="flex flex-col items-center" data-id="element-403">
                <span className="font-mono text-2xl md:text-3xl font-bold text-accent-primary leading-none tabular-nums" data-id="element-404">
                  {countdown.minutes}
                </span>
                <span className="font-ui text-[9px] uppercase tracking-widest text-text-muted mt-1" data-id="element-405">
                  Min
                </span>
              </div>
              <span className="font-mono text-xl text-accent-primary/50 -mt-3" data-id="element-406">
                :
              </span>
              <div className="flex flex-col items-center" data-id="element-407">
                <span className="font-mono text-2xl md:text-3xl font-bold text-accent-primary leading-none tabular-nums" data-id="element-408">
                  {countdown.seconds}
                </span>
                <span className="font-ui text-[9px] uppercase tracking-widest text-text-muted mt-1" data-id="element-409">
                  Sec
                </span>
              </div>
            </div>
          </div>

          <p className="font-ui text-xs text-text-muted tracking-wide" data-id="element-410">
            Instant digital access. Download in seconds.
          </p>

          {/* Social proof */}
          <div className="flex flex-col items-center gap-2 mt-6" data-id="element-411">
            <div className="flex items-center gap-1" data-id="element-412">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-accent-primary fill-accent-primary" data-id="element-413" />)}
            </div>
            <p className="font-ui text-xs text-text-muted tracking-wide" data-id="element-414">
              2,400+ readers · Rated 4.9/5
            </p>
          </div>
        </FadeIn>
      </div>

      <FadeIn delay={1.2} className="absolute bottom-8 left-1/2 -translate-x-1/2" data-id="element-415">
        <button onClick={scrollToNext} className="text-text-muted hover:text-accent-primary transition-colors duration-300 animate-bounce" aria-label="Scroll down" data-id="element-416">
          <ArrowDown className="w-6 h-6" data-id="element-417" />
        </button>
      </FadeIn>
    </section>;
}